import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import {
  FaChevronDown,
  FaChevronRight,
  FaClone,
  FaEdit,
  FaEye,
  FaLayerGroup,
  FaSearch,
  FaShieldAlt,
  FaTrash,
  FaUserCheck,
  FaUsersCog
} from "react-icons/fa";
import Sidebar from "../../components/Sidebar/sidebar";
import Header from "../../components/Header";
import { getUserPrivileges, getUserRole, isAuthenticated } from "../../utils/auth";
import { EMPLOYEE_MANAGEMENT_PRIVILEGES } from "../../utils/employeeManagementPrivileges";
import api from "../../utils/api";
import "./Roles.css";
import { API_BASE_URL } from '../../utils/apiBase';
import { confirmDialog } from '../../components/notifications/notificationService';

const emptyForm = {
  roleName: "",
  roleDescription: "",
  status: "active",
  privileges: []
};

const getModuleKey = (moduleName) => moduleName || "Other";

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("en-IN", {
    year: "numeric",
    month: "short",
    day: "2-digit"
  });
};

const Roles = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [roles, setRoles] = useState([]);
  const [privileges, setPrivileges] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingRole, setEditingRole] = useState(null);
  const [viewRole, setViewRole] = useState(null);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [roleSearch, setRoleSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [privilegeSearch, setPrivilegeSearch] = useState("");
  const [expandedModules, setExpandedModules] = useState({});
  const [copyRoleId, setCopyRoleId] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const userRole = getUserRole();
  const userPrivileges = getUserPrivileges();
  const isAdmin = userRole === "admin";
  const hasRolePrivilege = (key) => isAdmin || userPrivileges.includes(key);
  const rolePrivileges = EMPLOYEE_MANAGEMENT_PRIVILEGES.roles;
  const canCreateRole = hasRolePrivilege(rolePrivileges.create);
  const canViewRole = hasRolePrivilege(rolePrivileges.view);
  const canEditRole = hasRolePrivilege(rolePrivileges.edit);
  const canCloneRole = hasRolePrivilege(rolePrivileges.clone);
  const canDeleteRole = hasRolePrivilege(rolePrivileges.delete);
  const canAccessRoles = rolePrivileges.access.some(hasRolePrivilege);
  const canShowRoleActions = canViewRole || canEditRole || canCloneRole || canDeleteRole;

  const loadRoles = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = {};
      if (roleSearch.trim()) params.q = roleSearch.trim();
      if (statusFilter !== "all") params.status = statusFilter;
      const res = await api.get(`${API_BASE_URL}/api/roles`, { params });
      setRoles(res.data || []);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load roles.");
    } finally {
      setLoading(false);
    }
  }, [roleSearch, statusFilter]);

  useEffect(() => {
    const loadPrivileges = async () => {
      try {
        const res = await api.get(`${API_BASE_URL}/api/privileges`);
        setPrivileges(res.data || []);
      } catch (err) {
        setError(err.response?.data?.error || "Failed to load privileges.");
      }
    };

    loadPrivileges();
  }, []);

  useEffect(() => {
    const timer = setTimeout(loadRoles, 250);
    return () => clearTimeout(timer);
  }, [loadRoles]);

  useEffect(() => {
    const moduleNames = [...new Set(privileges.map((privilege) => getModuleKey(privilege.module)))];
    setExpandedModules((current) => {
      const next = { ...current };
      moduleNames.forEach((moduleName) => {
        if (next[moduleName] === undefined) {
          next[moduleName] = true;
        }
      });
      return next;
    });
  }, [privileges]);

  const selectedPrivilegeSet = useMemo(() => new Set(form.privileges || []), [form.privileges]);
  const roleStats = useMemo(() => {
    const activeRoles = roles.filter((role) => role.status === "active").length;
    const totalUsers = roles.reduce((sum, role) => sum + (role.userCount || 0), 0);

    return {
      total: roles.length,
      active: activeRoles,
      inactive: roles.length - activeRoles,
      totalUsers,
      selectedPrivileges: form.privileges.length
    };
  }, [roles, form.privileges.length]);

  const groupedPrivileges = useMemo(() => {
    const grouped = privileges.reduce((acc, privilege) => {
      const module = getModuleKey(privilege.module);

      if (!acc[module]) {
        acc[module] = {
          rootPrivileges: [],
          childMap: {}
        };
      }

      if (privilege.parentKey) {
        if (!acc[module].childMap[privilege.parentKey]) {
          acc[module].childMap[privilege.parentKey] = [];
        }
        acc[module].childMap[privilege.parentKey].push(privilege);
      } else {
        acc[module].rootPrivileges.push(privilege);
      }

      return acc;
    }, {});

    Object.values(grouped).forEach((group) => {
      group.rootPrivileges.sort((a, b) => a.label.localeCompare(b.label));
      Object.values(group.childMap).forEach((children) =>
        children.sort((a, b) => a.label.localeCompare(b.label))
      );
    });

    return grouped;
  }, [privileges]);

  const moduleNames = useMemo(
    () => Object.keys(groupedPrivileges).sort((a, b) => a.localeCompare(b)),
    [groupedPrivileges]
  );

  const getChildren = useCallback(
    (moduleName, parentKey) => groupedPrivileges[moduleName]?.childMap[parentKey] || [],
    [groupedPrivileges]
  );

  const getModulePrivilegeKeys = useCallback((moduleName) => {
    const group = groupedPrivileges[moduleName];
    if (!group) return [];

    return group.rootPrivileges.flatMap((privilege) => [
      privilege.key,
      ...(group.childMap[privilege.key] || []).map((child) => child.key)
    ]);
  }, [groupedPrivileges]);

  const setPrivilegesForForm = (nextPrivileges) => {
    setForm((current) => ({
      ...current,
      privileges: [...new Set(nextPrivileges)]
    }));
  };

  const toggleParent = (moduleName, privilege) => {
    const children = getChildren(moduleName, privilege.key);
    const relatedKeys = [privilege.key, ...children.map((child) => child.key)];
    const selected = relatedKeys.every((key) => selectedPrivilegeSet.has(key));

    setPrivilegesForForm(
      selected
        ? form.privileges.filter((key) => !relatedKeys.includes(key))
        : [...form.privileges, ...relatedKeys]
    );
  };

  const toggleChild = (moduleName, parentKey, childKey) => {
    const children = getChildren(moduleName, parentKey);
    const childKeys = children.map((child) => child.key);
    const nextSet = new Set(form.privileges || []);

    if (nextSet.has(childKey)) {
      nextSet.delete(childKey);
    } else {
      nextSet.add(parentKey);
      nextSet.add(childKey);
    }

    const selectedChildrenCount = childKeys.filter((key) => nextSet.has(key)).length;
    if (selectedChildrenCount === 0) {
      nextSet.delete(parentKey);
    } else {
      nextSet.add(parentKey);
    }

    setPrivilegesForForm([...nextSet]);
  };

  const selectModule = (moduleName) => {
    setPrivilegesForForm([...form.privileges, ...getModulePrivilegeKeys(moduleName)]);
  };

  const deselectModule = (moduleName) => {
    const moduleKeys = new Set(getModulePrivilegeKeys(moduleName));
    setPrivilegesForForm(form.privileges.filter((key) => !moduleKeys.has(key)));
  };

  const resetForm = () => {
    setForm(emptyForm);
    setEditingRole(null);
    setCopyRoleId("");
  };

  const openCreateRole = () => {
    resetForm();
    setViewRole(null);
    setShowRoleModal(true);
  };

  const closeRoleModal = () => {
    setShowRoleModal(false);
    resetForm();
  };

  const editRole = (role) => {
    setEditingRole(role);
    setViewRole(null);
    setShowRoleModal(true);
    setForm({
      roleName: role.roleName || "",
      roleDescription: role.roleDescription || "",
      status: role.status || "active",
      privileges: role.privileges || []
    });
  };

  const copyPrivileges = (roleId) => {
    setCopyRoleId(roleId);
    const role = roles.find((item) => item._id === roleId);
    if (role) {
      setPrivilegesForForm(role.privileges || []);
    }
  };

  const submitRole = async (e) => {
    e.preventDefault();
    if (!form.roleName.trim()) {
      setError("Role name is required.");
      return;
    }

    setSaving(true);
    setError("");

    try {
      const payload = {
        roleName: form.roleName,
        roleDescription: form.roleDescription,
        status: form.status,
        privileges: form.privileges
      };

      if (editingRole) {
        const res = await api.put(`${API_BASE_URL}/api/roles/${editingRole._id}`, payload);
        setRoles((current) => current.map((role) => (role._id === res.data._id ? res.data : role)));
      } else {
        const res = await api.post(`${API_BASE_URL}/api/roles`, payload);
        setRoles((current) => [res.data, ...current]);
      }

      resetForm();
      setShowRoleModal(false);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to save role.");
    } finally {
      setSaving(false);
    }
  };

  const cloneRole = async (role) => {
    const roleName = window.prompt("Enter new role name", `${role.roleName} Copy`);
    if (!roleName) return;

    try {
      const res = await api.post(`${API_BASE_URL}/api/roles/${role._id}/clone`, { roleName });
      setRoles((current) => [res.data, ...current]);
    } catch (err) {
      alert(err.response?.data?.error || "Failed to clone role.");
    }
  };

  const updateStatus = async (role) => {
    const nextStatus = role.status === "active" ? "inactive" : "active";
    try {
      const res = await api.patch(`${API_BASE_URL}/api/roles/${role._id}/status`, {
        status: nextStatus
      });
      setRoles((current) => current.map((item) => (item._id === res.data._id ? res.data : item)));
    } catch (err) {
      alert(err.response?.data?.error || "Failed to update role status.");
    }
  };

  const deleteRole = async (role) => {
    if (!await confirmDialog({ title: `Delete ${role.roleName}?`, message: 'This role will be permanently removed. This action cannot be undone.', confirmText: 'Delete Role' })) return;

    try {
      await api.delete(`${API_BASE_URL}/api/roles/${role._id}`);
      setRoles((current) => current.filter((item) => item._id !== role._id));
      if (editingRole?._id === role._id) {
        resetForm();
      }
    } catch (err) {
      alert(err.response?.data?.error || "Failed to delete role.");
    }
  };

  const filteredModules = useMemo(() => {
    const search = privilegeSearch.trim().toLowerCase();

    return moduleNames
      .map((moduleName) => {
        const group = groupedPrivileges[moduleName];
        const rootPrivileges = group.rootPrivileges
          .map((privilege) => {
            const children = getChildren(moduleName, privilege.key);
            const searchableText = [
              moduleName,
              privilege.label,
              privilege.key,
              ...children.flatMap((child) => [child.label, child.key])
            ]
              .join(" ")
              .toLowerCase();

            if (!search || searchableText.includes(search)) {
              return {
                ...privilege,
                children: !search
                  ? children
                  : children.filter((child) =>
                    [child.label, child.key, moduleName, privilege.label]
                      .join(" ")
                      .toLowerCase()
                      .includes(search)
                  )
              };
            }

            return null;
          })
          .filter(Boolean);

        return { moduleName, rootPrivileges };
      })
      .filter((module) => module.rootPrivileges.length > 0);
  }, [moduleNames, groupedPrivileges, privilegeSearch, getChildren]);

  if (!isAuthenticated()) {
    return <Navigate to="/login" replace />;
  }

  if (!canAccessRoles) {
    return <Navigate to="/unauthorized" replace />;
  }

  return (
    <div className="roles-container">
      <div className="sidebar">
        <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />
      </div>

      <div className={`roles-content ${collapsed ? "collapsed" : ""}`}>
        <div className="roles-sticky-header">
          <Header />
        </div>

        <div className="roles-scroll-area">
          <section className="roles-shell">
            <div className="roles-heading">
              <div>
                <span><FaShieldAlt /> Administration</span>
                <h2>Roles</h2>
                <p>Create custom roles, assign structured privileges, and manage access from one place.</p>
              </div>
              <div className="roles-stats">
                <strong>{roles.length}</strong>
                <span>roles</span>
              </div>
            </div>

            <div className="roles-summary-grid">
              <div className="roles-summary-card is-blue">
                <FaUsersCog />
                <span>Total Roles</span>
                <strong>{roleStats.total}</strong>
                <small>configured access profiles</small>
              </div>
              <div className="roles-summary-card is-green">
                <FaUserCheck />
                <span>Active Roles</span>
                <strong>{roleStats.active}</strong>
                <small>{roleStats.inactive} inactive</small>
              </div>
              <div className="roles-summary-card is-amber">
                <FaLayerGroup />
                <span>Selected Privileges</span>
                <strong>{roleStats.selectedPrivileges}</strong>
                <small>in current draft</small>
              </div>
              <div className="roles-summary-card is-indigo">
                <FaShieldAlt />
                <span>Assigned Users</span>
                <strong>{roleStats.totalUsers}</strong>
                <small>across listed roles</small>
              </div>
            </div>

            {error && <p className="roles-error">{error}</p>}

            <div className="roles-layout roles-list-layout">
              <aside className="roles-list-panel">
                <div className="roles-list-toolbar">
                  <label className="roles-search">
                    <FaSearch />
                    <input
                      value={roleSearch}
                      onChange={(e) => setRoleSearch(e.target.value)}
                      placeholder="Search roles"
                    />
                  </label>
                  <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                    <option value="all">All Status</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                  {canCreateRole && (
                    <button type="button" className="roles-create-btn" onClick={openCreateRole}>
                      Create Role
                    </button>
                  )}
                </div>

                <div className="roles-table-wrap">
                  <table className="roles-table">
                    <thead>
                      <tr>
                        <th>Role Name</th>
                        <th>Description</th>
                        <th>Privileges</th>
                        <th>Users</th>
                        <th>Status</th>
                        <th>Created By</th>
                        <th>Created Date</th>
                        {canShowRoleActions && <th>Actions</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan={canShowRoleActions ? 8 : 7} className="roles-empty">Loading roles...</td>
                        </tr>
                      ) : roles.length === 0 ? (
                        <tr>
                          <td colSpan={canShowRoleActions ? 8 : 7} className="roles-empty">No roles found.</td>
                        </tr>
                      ) : (
                        roles.map((role) => (
                          <tr key={role._id} className={editingRole?._id === role._id ? "selected-role-row" : ""}>
                            <td><strong>{role.roleName}</strong></td>
                            <td>{role.roleDescription || "-"}</td>
                            <td>{role.privileges?.length || 0}</td>
                            <td>{role.userCount || 0}</td>
                            <td>
                              <button
                                type="button"
                                className={`role-status ${role.status}`}
                                onClick={canEditRole ? () => updateStatus(role) : undefined}
                                disabled={!canEditRole}
                              >
                                {role.status === "active" ? "Active" : "Inactive"}
                              </button>
                            </td>
                            <td>{role.createdBy?.name || "-"}</td>
                            <td>{formatDate(role.createdAt)}</td>
                            {canShowRoleActions && <td>
                              <div className="role-actions">
                                {canViewRole && (
                                  <button type="button" title="View" onClick={() => setViewRole(role)}>
                                    <FaEye />
                                  </button>
                                )}
                                {canEditRole && (
                                  <button type="button" title="Edit" onClick={() => editRole(role)}>
                                    <FaEdit />
                                  </button>
                                )}
                                {canCloneRole && (
                                  <button type="button" title="Clone" onClick={() => cloneRole(role)}>
                                    <FaClone />
                                  </button>
                                )}
                                {canDeleteRole && (
                                  <button type="button" title="Delete" onClick={() => deleteRole(role)}>
                                    <FaTrash />
                                  </button>
                                )}
                              </div>
                            </td>}
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </aside>
            </div>
          </section>
        </div>
      </div>

      {showRoleModal && (
        <div className="role-modal-overlay">
          <form className="role-form-panel role-form-modal" onSubmit={submitRole}>
            <div className="panel-title-row">
              <div>
                <h3>{editingRole ? "Edit Role" : "Create Role"}</h3>
                <p>{form.privileges.length} privileges selected</p>
              </div>
              <div>
                {editingRole && canCreateRole && (
                  <button type="button" onClick={openCreateRole}>
                    New Role
                  </button>
                )}
                <button type="button" onClick={closeRoleModal}>
                  Close
                </button>
              </div>
            </div>

            <div className="role-form-grid">
              <label>
                Role Name
                <input
                  value={form.roleName}
                  onChange={(e) => setForm({ ...form, roleName: e.target.value })}
                  placeholder="Claims Manager"
                />
              </label>

              <label>
                Status
                <select
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </label>

              <label className="full">
                Description
                <textarea
                  value={form.roleDescription}
                  onChange={(e) => setForm({ ...form, roleDescription: e.target.value })}
                  placeholder="Optional role purpose or access notes"
                />
              </label>
            </div>

            <div className="role-toolbar">
              <label className="roles-search">
                <FaSearch />
                <input
                  value={privilegeSearch}
                  onChange={(e) => setPrivilegeSearch(e.target.value)}
                  placeholder="Search privileges"
                />
              </label>
              <select value={copyRoleId} onChange={(e) => copyPrivileges(e.target.value)}>
                <option value="">Copy privileges from role</option>
                {roles.map((role) => (
                  <option key={role._id} value={role._id}>
                    {role.roleName}
                  </option>
                ))}
              </select>
              <button type="button" onClick={() => setPrivilegesForForm(privileges.map((item) => item.key))}>
                Select All
              </button>
              <button type="button" onClick={() => setPrivilegesForForm([])}>
                Deselect All
              </button>
            </div>

            <div className="role-privileges">
              {filteredModules.map(({ moduleName, rootPrivileges }) => {
                const moduleKeys = getModulePrivilegeKeys(moduleName);
                const selectedCount = moduleKeys.filter((key) => selectedPrivilegeSet.has(key)).length;
                const isExpanded = expandedModules[moduleName] !== false;

                return (
                  <article key={moduleName} className="role-module">
                    <header className="role-module-header">
                      <button
                        type="button"
                        onClick={() =>
                          setExpandedModules((current) => ({
                            ...current,
                            [moduleName]: !isExpanded
                          }))
                        }
                      >
                        {isExpanded ? <FaChevronDown /> : <FaChevronRight />}
                        {moduleName}
                      </button>
                      <div>
                        <span>{selectedCount}/{moduleKeys.length}</span>
                        <button type="button" onClick={() => selectModule(moduleName)}>
                          Select
                        </button>
                        <button type="button" onClick={() => deselectModule(moduleName)}>
                          Clear
                        </button>
                      </div>
                    </header>

                    <div className={`role-module-body ${isExpanded ? "open" : ""}`}>
                      {rootPrivileges.map((privilege) => {
                        const children = privilege.children || [];
                        const childKeys = children.map((child) => child.key);
                        const selectedChildren = childKeys.filter((key) => selectedPrivilegeSet.has(key)).length;
                        const parentSelected = selectedPrivilegeSet.has(privilege.key);
                        const checked = childKeys.length
                          ? parentSelected && selectedChildren === childKeys.length
                          : parentSelected;
                        const partial = childKeys.length > 0 && (parentSelected || selectedChildren > 0) && !checked;

                        return (
                          <div key={privilege.key} className="role-parent-row">
                            <label className="role-checkline">
                              <input
                                type="checkbox"
                                checked={checked}
                                ref={(input) => {
                                  if (input) input.indeterminate = partial;
                                }}
                                onChange={() => toggleParent(moduleName, privilege)}
                              />
                              <span>
                                <strong>{privilege.label}</strong>
                                <small>{privilege.key}</small>
                              </span>
                            </label>

                            {children.length > 0 && (
                              <div className="role-child-list">
                                {children.map((child) => (
                                  <label key={child.key} className="role-checkline child">
                                    <input
                                      type="checkbox"
                                      checked={selectedPrivilegeSet.has(child.key)}
                                      onChange={() => toggleChild(moduleName, privilege.key, child.key)}
                                    />
                                    <span>
                                      <strong>{child.label}</strong>
                                      <small>{child.key}</small>
                                    </span>
                                  </label>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </article>
                );
              })}
            </div>

            <div className="role-form-actions">
              <button type="button" onClick={resetForm}>
                Reset
              </button>
              <button type="submit" className="primary" disabled={saving || (editingRole ? !canEditRole : !canCreateRole)}>
                {saving ? "Saving..." : editingRole ? "Update Role" : "Create Role"}
              </button>
            </div>
          </form>
        </div>
      )}

      {viewRole && (
        <div className="role-modal-overlay">
          <div className="role-modal">
            <div className="role-modal-header">
              <div>
                <h3>{viewRole.roleName}</h3>
                <p>{viewRole.roleDescription || "No description"}</p>
              </div>
              <button type="button" onClick={() => setViewRole(null)}>Close</button>
            </div>
            <div className="role-summary-preview">
              <span>{viewRole.privileges?.length || 0} privileges</span>
              <span>{viewRole.userCount || 0} users</span>
              <span>{viewRole.status}</span>
            </div>
            <div className="role-preview-list">
              {(viewRole.privileges || []).map((key) => {
                const privilege = privileges.find((item) => item.key === key);
                return (
                  <div key={key}>
                    <strong>{privilege?.label || key}</strong>
                    <small>{key}</small>
                  </div>
                );
              })}
              {(!viewRole.privileges || viewRole.privileges.length === 0) && (
                <p>No privileges assigned.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Roles;
